/* eslint-disable react/no-unstable-nested-components */
import { useContext, useState } from 'react'
import { object, func, bool } from 'prop-types'
import { components } from '@digital-commerce/shared-utils'
import { Typography, StackView, FlexGrid, Link, Spacer, Box } from '@telus-uds/components-web'
import Icons from '@telus-uds/palette-allium/build/web/icons'

import localeContext from '../../../contexts/Locale'
import useTranslate from '../../../hooks/useTranslate'
import useMessages from '../../../hooks/useMessages'

import useCart from '../../../hooks/useCart'
import ConfirmationModal from '../ConfirmationModal'
import GeneralMessage from '../GeneralMessage'
import { hasGift } from '../../../utils/cart'

import { alloyEvents } from '../../../utils/alloyAnalytics'
import {
  onShowDetailsClick,
  onKeepItemDialogClick,
  onRemoveItemClick,
  onCloseItemDialogClick,
  onGiftReminder
} from '../../../utils/legacyAnalytics'
import { hasActiveContract, shouldRenderRemoveFinancedOfferBanner } from '../../../utils/mobility'
import useAuthentication from '../../../hooks/useAuthentication'
import useLegacyAnalytics from '../../../hooks/useLegacyAnalytics'

const ActionLinks = ({
  toggleOfferDetail,
  isDetailVisible,
  cartItem, // passed in by CartItem.jsx
  isRemoveButtonHidden,
  isShowDetailsCtaHidden,
  hasFinancedAccessoryGroup
}) => {
  const [isConfirmationModelVisible, setIsConfirmationModelVisible] = useState(false)
  const translate = useTranslate()
  const { lang } = useContext(localeContext)
  const { cart, removeItems } = useCart()
  const { updateLegacyDataLayer } = useLegacyAnalytics()

  const { addMessage, removeMessage } = useMessages()
  const isAuthenticated = useAuthentication()

  const handleShowDetailsClick = (e) => {
    e.preventDefault()
    if (!isDetailVisible) {
      updateLegacyDataLayer(onShowDetailsClick(cartItem))
      alloyEvents.showDetails(cartItem)
    }
    toggleOfferDetail()
  }

  const handleRemoveItemClick = async (e) => {
    e.preventDefault()
    setIsConfirmationModelVisible(true)
  }

  const handleConfirmationModelCancel = () => {
    setIsConfirmationModelVisible(false)
    updateLegacyDataLayer(onKeepItemDialogClick(cartItem))
  }

  const handleConfirmationModelClose = () => {
    setIsConfirmationModelVisible(false)
    updateLegacyDataLayer(onCloseItemDialogClick(cartItem))
  }

  const handleConfirmationModelConfirm = (e) => {
    e.preventDefault()
    setIsConfirmationModelVisible(false)
    removeItems({
      items: [cartItem],
      postRemoval: () => {
        if (hasGift([cartItem])) {
          updateLegacyDataLayer(onGiftReminder)
          addMessage({
            id: 'gift-removal-message',
            node: (
              <GeneralMessage
                lang={lang}
                variant="success"
                dismissable
                onDismiss={() => removeMessage('gift-removal-message')}
                text={translate('appNotification.giftRemovalNotification.success.message')}
                actions={
                  <Link
                    href="/shop/home-services/bundle/build-your-own"
                    variant={{ style: 'inline', size: 'small' }}
                  >
                    {translate('appNotification.giftRemovalNotification.success.cta')}
                  </Link>
                }
              />
            )
          })
        }

        if (shouldRenderRemoveFinancedOfferBanner(cartItem, hasFinancedAccessoryGroup)) {
          const messagePath =
            isAuthenticated && hasActiveContract(cartItem) ? 'existingContract' : 'newContract'

          addMessage({
            id: 'financed-accessory-removal-message',
            node: (
              <GeneralMessage
                lang={lang}
                variant="instructional"
                dismissable
                onDismiss={() => removeMessage('financed-accessory-removal-message')}
                text={translate(`accessoryFinancing.removeOfferBanner.${messagePath}`)}
              />
            )
          })
        }

        updateLegacyDataLayer(onRemoveItemClick(cartItem))
        alloyEvents.removeFromCart({ product: cartItem, cart })
      }
    })
  }

  const renderBodyText = ['UPC', 'ESS'].includes(cartItem.provisioner)
    ? ''
    : translate('confirmModal.bodyText')

  return (
    <Box left={{ xs: 0, md: 3 }}>
      <FlexGrid.Row>
        <FlexGrid.Col xs={12} sm={12} md={12} lg={10} lgOffset={2} xlOffset={2} xl={10}>
          <StackView
            direction="row"
            tokens={{
              justifyContent: 'space-between',
              flexGrow: 1
            }}
          >
            <Typography variant={{ size: 'small' }}>
              <components.ShouldRender when={!isShowDetailsCtaHidden}>
                {() => (
                  <Link
                    href="#"
                    icon={isDetailVisible ? Icons.CaretUp : Icons.CaretDown}
                    onClick={handleShowDetailsClick}
                    testID="ActionLinks-toggleDetails"
                    variant={{ style: 'inline', size: 'small' }}
                  >
                    {isDetailVisible
                      ? translate('hideDetailsLinkText')
                      : translate('showDetailsLinkText')}
                  </Link>
                )}
              </components.ShouldRender>
            </Typography>
            <components.ShouldRender when={!isRemoveButtonHidden}>
              {() => (
                <Typography variant={{ size: 'small' }}>
                  <Link
                    href="#"
                    icon={Icons.Delete}
                    onClick={handleRemoveItemClick}
                    testID="ActionLinks-removeItem"
                    variant={{ style: 'inline', size: 'small' }}
                  >
                    {translate('removeLinkText')}
                  </Link>
                </Typography>
              )}
            </components.ShouldRender>
          </StackView>
          <Spacer space={1} />
        </FlexGrid.Col>
      </FlexGrid.Row>
      <ConfirmationModal
        title={translate('confirmModal.dialogHeading')}
        text={renderBodyText}
        cancelButtonText={translate('confirmModal.cancelButton')}
        confirmButtonText={translate('confirmModal.confirmButton')}
        titleA11yText={translate('confirmModal.dialogHeadingA11y')}
        visible={isConfirmationModelVisible}
        handleCancel={handleConfirmationModelCancel}
        handleConfirm={handleConfirmationModelConfirm}
        handleClose={handleConfirmationModelClose}
      />
    </Box>
  )
}

ActionLinks.propTypes = {
  toggleOfferDetail: func,
  isDetailVisible: bool,
  cartItem: object,
  isRemoveButtonHidden: bool,
  isShowDetailsCtaHidden: bool,
  hasFinancedAccessoryGroup: bool
}

ActionLinks.defaultProps = {
  toggleOfferDetail: undefined,
  cartItem: {},
  isDetailVisible: false,
  isRemoveButtonHidden: false,
  isShowDetailsCtaHidden: false,
  hasFinancedAccessoryGroup: false
}

export default ActionLinks
